import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { exportRowsToCsv } from "../lib/exporters";

interface ClienteRead {
  cliente_id: string;
  codigo_externo: string;
  direccion: string;
  estado_servicio: string;
  outside_dma_warning?: boolean;
}

const ESTADO_BADGE: Record<string, string> = {
  activo: "badge badge--accent",
  suspendido: "badge badge--warn",
  cortado: "badge badge--danger",
};

export function SupervisorCatastro(): JSX.Element {
  const { toast } = useToast();
  const query = useQuery<ClienteRead[]>({
    queryKey: ["clientes.list"],
    queryFn: () => api<ClienteRead[]>("/clientes?limit=50"),
  });

  const data = query.data ?? [];
  const activos = data.filter((c) => c.estado_servicio === "activo").length;
  const fueraDma = data.filter((c) => c.outside_dma_warning).length;

  return (
    <>
      <PageHeader
        eyebrow="Supervisor · R2.*"
        title="Catastro"
        subtitle="Clientes, medidores, DMAs y asociaciones — base operacional del sistema."
        actions={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => {
                exportRowsToCsv(
                  `catastro-clientes-${new Date().toISOString().slice(0, 10)}.csv`,
                  data.map((c) => ({
                    codigo: c.codigo_externo,
                    direccion: c.direccion,
                    estado: c.estado_servicio,
                    aviso_dma: c.outside_dma_warning ? "si" : "no",
                  })),
                );
                toast(`Exportados ${data.length} clientes`, "success");
              }}
            >
              <Icon name="report" size={13} /> Exportar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => toast("Alta de cliente no disponible en modo demo", "info")}
            >
              <Icon name="plus" size={13} /> Nuevo cliente
            </button>
          </>
        }
      />

      <div className="card-grid" style={{ marginBottom: 24 }}>
        <div className="kpi">
          <span className="kpi__label">Total clientes</span>
          <span className="kpi__value">{data.length}</span>
          <span className="kpi__hint">en la página actual</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Activos</span>
          <span className="kpi__value">{activos}</span>
          <span className="kpi__hint">estado_servicio = activo</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">Avisos DMA</span>
          <span className="kpi__value">{fueraDma}</span>
          <span className="kpi__hint">punto fuera del polígono</span>
        </div>
      </div>

      {query.isLoading && (
        <div className="status-block status-block--info">Cargando catastro…</div>
      )}

      {query.data && (
        <section className="card">
          <h2 className="card__title">Clientes</h2>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Dirección</th>
                  <th>Estado servicio</th>
                  <th>Aviso DMA</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((c) => (
                  <tr key={c.cliente_id}>
                    <td className="text-mono">{c.codigo_externo}</td>
                    <td>{c.direccion}</td>
                    <td>
                      <span
                        className={
                          ESTADO_BADGE[c.estado_servicio] ?? "badge badge--neutral"
                        }
                      >
                        {c.estado_servicio}
                      </span>
                    </td>
                    <td>
                      {c.outside_dma_warning ? (
                        <span className="badge badge--warn">fuera de DMA</span>
                      ) : (
                        <span className="badge badge--accent">ok</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

export function SupervisorParametros(): JSX.Element {
  const { toast } = useToast();
  const cambios: Array<[string, string, number, string, string, string]> = [
    ["anomaly.consumo_cero.dias", "global", 3, "60", "demo", "2025-05-12"],
    ["anomaly.caida_brusca.pct", "global", 2, "50", "demo", "2025-04-30"],
    ["ilicit.umbral", "global", 4, "70", "demo", "2025-05-02"],
    ["anomaly.alza_brusca.pct", "dma:DMA-001", 1, "180", "demo", "2025-05-08"],
  ];
  return (
    <>
      <PageHeader
        eyebrow="Supervisor · R12.1 / R12.2"
        title="Parámetros"
        subtitle="Configuración versionada de umbrales, pesos del scoring y plazos. Append-only con historial completo."
        actions={
          <button
            className="btn"
            onClick={() => {
              exportRowsToCsv(
                "parametros-historial.csv",
                cambios.map(([key, scope, ver, val, author, since]) => ({
                  parametro: key,
                  scope,
                  version: ver,
                  valor: val,
                  autor: author,
                  vigente_desde: since,
                })),
              );
              toast("Historial de parámetros exportado", "success");
            }}
          >
            <Icon name="report" size={13} /> Exportar historial
          </button>
        }
      />

      <div className="card-grid" style={{ marginBottom: 24 }}>
        <div className="kpi">
          <span className="kpi__label">Parámetros activos</span>
          <span className="kpi__value">42</span>
          <span className="kpi__hint">global + overrides por DMA</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Versión vigente</span>
          <span className="kpi__value">v3</span>
          <span className="kpi__hint">publicada hace 6 días</span>
        </div>
      </div>

      <section className="card">
        <h2 className="card__title">Cambios recientes</h2>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Parámetro</th>
                <th>Scope</th>
                <th>Versión</th>
                <th>Valor</th>
                <th>Autor</th>
                <th>Vigente desde</th>
              </tr>
            </thead>
            <tbody>
              {cambios.map(([key, scope, ver, val, author, since]) => (
                <tr key={`${key}-${ver}`}>
                  <td className="text-mono">{key}</td>
                  <td>
                    <span className="badge badge--info">{scope}</span>
                  </td>
                  <td className="numeric">v{ver}</td>
                  <td className="text-mono">{val}</td>
                  <td>{author}</td>
                  <td className="text-mono">{since}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export function SupervisorAuditoria(): JSX.Element {
  const { toast } = useToast();
  const eventos: Array<[string, string, string, string, string]> = [
    ["2025-06-15 12:03", "demo", "login", "session", "success"],
    ["2025-06-15 11:58", "demo", "report.generate", "nrw_por_periodo", "success"],
    ["2025-06-15 11:30", "operador", "config.change", "parametros", "success"],
    ["2025-06-15 10:21", "anonymous", "alerta.assign", "alerta:42", "denied"],
  ];
  return (
    <>
      <PageHeader
        eyebrow="Supervisor · R10.3 / R10.4"
        title="Auditoría"
        subtitle="Cadena hash append-only de eventos sensibles, verificable end-to-end."
        actions={
          <>
            <button
              className="btn"
              onClick={() => {
                exportRowsToCsv(
                  "audit-log.csv",
                  eventos.map(([ts, actor, accion, recurso, resultado]) => ({
                    timestamp: ts,
                    actor,
                    accion,
                    recurso,
                    resultado,
                  })),
                );
                toast("Audit log exportado (firmado)", "success");
              }}
            >
              <Icon name="report" size={13} /> Exportar
            </button>
            <button
              className="btn btn--primary"
              onClick={() => toast("Verificación de cadena: OK · 214 eventos íntegros", "success")}
            >
              <Icon name="shield" size={13} /> Verificar cadena
            </button>
          </>
        }
      />

      <div className="card-grid" style={{ marginBottom: 24 }}>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Estado de la cadena</span>
          <span className="kpi__value">OK</span>
          <span className="kpi__hint">Última verificación: hace 12 min</span>
        </div>
        <div className="kpi">
          <span className="kpi__label">Eventos del último día</span>
          <span className="kpi__value">214</span>
          <span className="kpi__hint">login, config, alertas, exports</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">Intentos denegados</span>
          <span className="kpi__value">5</span>
          <span className="kpi__hint">requieren revisión</span>
        </div>
      </div>

      <section className="card">
        <h2 className="card__title">Últimos eventos</h2>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Actor</th>
                <th>Acción</th>
                <th>Recurso</th>
                <th>Resultado</th>
                <th>Hash</th>
              </tr>
            </thead>
            <tbody>
              {eventos.map(([ts, actor, accion, recurso, resultado], idx) => (
                <tr key={`${ts}-${idx}`}>
                  <td className="text-mono">{ts}</td>
                  <td>{actor}</td>
                  <td>{accion}</td>
                  <td className="text-mono">{recurso}</td>
                  <td>
                    <span
                      className={
                        resultado === "success"
                          ? "badge badge--accent"
                          : resultado === "denied"
                            ? "badge badge--danger"
                            : "badge badge--neutral"
                      }
                    >
                      {resultado}
                    </span>
                  </td>
                  <td className="text-mono">
                    {`a${idx + 1}b9c…${(idx + 1) * 7}f`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
